// src/components/CaseStudyCard.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { imageMap } from '../utils/imageImports';

// Animation for skeleton loading
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// Animation for fade in
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Main card container with better hover effects and proper z-index
const CardContainer = styled(Link)`
  display: block;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 0;
  overflow: hidden;
  text-decoration: none !important; /* Force no underline */
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  transition: all ${({ theme }) => theme.transitions.base};
  position: relative;
  z-index: 1; /* Keep cards below header */
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.secondary};
    z-index: 2; /* Still below header on hover */
    text-decoration: none !important; /* Force no underline on hover */
    
    .view-link {
      color: ${({ theme }) => theme.colors.secondary};
      
      svg {
        transform: translateX(4px);
      }
    }
  }
  
  &:active {
    transform: translateY(-2px);
  }
  
  /* Prevent all child elements from showing underlines */
  * {
    text-decoration: none !important;
  }
`;

// Thumbnail wrapper with better aspect ratio
// $diagram: a vector diagram is shown whole rather than cropped, so the letterbox
// matches the diagrams' own canvas colour and the fit reads as intentional.
const ThumbnailWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 62.5%; // 16:10 aspect ratio
  overflow: hidden;
  background: ${({ theme, $diagram }) => $diagram
    ? theme.colors.background
    : theme.colors.backgroundSecondary};
`;

// Loading skeleton for thumbnail
const ThumbnailSkeleton = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.backgroundSecondary} 0%,
    ${({ theme }) => theme.colors.surface} 50%,
    ${({ theme }) => theme.colors.backgroundSecondary} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 2s infinite;
`;

// Fallback for missing thumbnails
const ThumbnailFallback = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.secondary}40,
    ${({ theme }) => theme.colors.accent}60
  );
  color: ${({ theme }) => theme.colors.background};
  font-size: 3rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-family: ${({ theme }) => theme.fonts.heading};
`;

// Thumbnail image with fade-in animation
const Thumbnail = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* Photographs fill the card and may crop. Diagrams carry text, so cropping one
     cuts a heading mid-word; they are fitted whole instead. */
  object-fit: ${({ $diagram }) => $diagram ? 'contain' : 'cover'};
  padding: ${({ $diagram, theme }) => $diagram ? theme.spacing.xs : '0'};
  box-sizing: border-box;
  transition: transform ${({ theme }) => theme.transitions.slow};
  opacity: ${({ $loaded }) => $loaded ? 1 : 0};
  animation: ${({ $loaded }) => $loaded ? fadeIn : 'none'} 0.4s ease-out;

  /* A fitted diagram already touches the card edges, so a large zoom would clip
     the very labels the fit exists to preserve. */
  ${CardContainer}:hover & {
    transform: scale(${({ $diagram }) => $diagram ? 1.03 : 1.1});
  }
`;

const VideoThumbnail = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// Blur placeholder for progressive loading
const BlurPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${({ $src }) => $src ? `url(${$src})` : 'none'};
  background-size: cover;
  background-position: center;
  filter: blur(20px);
  transform: scale(1.1);
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: opacity 0.3s ease-out;
`;

// Card content area
// The title leads, above the image, so the case is named before it is illustrated.
const CardHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg}
    ${({ theme }) => theme.spacing.base};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.base}
      ${({ theme }) => theme.spacing.sm};
  }
`;

const CardContent = styled.div`
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg}
    ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.base};
  }
`;

// Company badge
const CompanyBadge = styled.div`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.textTertiary};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

// Card title
const CardTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  line-height: ${({ theme }) => theme.lineHeights.tight};
  letter-spacing: ${({ theme }) => theme.letterSpacing.tight};
  text-decoration: none !important;
  
  &:hover {
    text-decoration: none !important;
  }
`;

// Card description
const CardDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-decoration: none !important;
  
  &:hover {
    text-decoration: none !important;
  }
`;

// View link
const ViewLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  text-decoration: none !important;
  
  svg {
    width: 16px;
    height: 16px;
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
  
  &:hover {
    text-decoration: none !important;
  }
`;

// Helper function to check if URL is a video
const isVideo = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
  return videoExtensions.some(ext => url.toLowerCase().includes(ext));
};

// Helper to generate low quality placeholder
const getLowQualityPlaceholder = (thumbnailPath) => {
  // If you have low-quality placeholders, use them
  // Otherwise, return null and use skeleton
  return null;
};

const CaseStudyCard = ({ study }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const cardRef = useRef(null);
  const imageRef = useRef(null);
  
  // Prefer a card-sized image when the case supplies one. A hero diagram drawn for a
  // full-width column is unreadable at card size, so card_thumbnail carries a legible
  // stand-in; the detail page still shows the real diagram.
  const cardImage = study.card_thumbnail || study.thumbnail;
  const thumbnailSrc = imageMap[cardImage] || cardImage;
  const placeholderSrc = getLowQualityPlaceholder(cardImage);

  // A vector thumbnail is a diagram whose labels are part of its meaning, so it is
  // fitted whole rather than cropped to fill. Photographs keep the fill behaviour.
  const isDiagram = /\.svg(\?|#|$)/i.test(cardImage || '');
  
  // Implement Intersection Observer for true lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px' // Start loading 50px before entering viewport
      }
    );

    const currentCard = cardRef.current;
    if (currentCard) {
      observer.observe(currentCard);
    }

    return () => {
      if (currentCard) {
        observer.unobserve(currentCard);
      }
    };
  }, []);

  // Preload image when in view
  useEffect(() => {
    if (isInView && thumbnailSrc && !isVideo(thumbnailSrc)) {
      const img = new Image();
      img.src = thumbnailSrc;
      img.onload = () => {
        setImageLoaded(true);
      };
      img.onerror = () => {
        setImageError(true);
      };
    }
  }, [isInView, thumbnailSrc]);
  
  return (
    <CardContainer to={`/case-study/${study.id}`} ref={cardRef}>
      <CardHeader>
        {study.company && (
          <CompanyBadge>{study.company}</CompanyBadge>
        )}
        <CardTitle>{study.title}</CardTitle>
      </CardHeader>

      <ThumbnailWrapper $diagram={isDiagram}>
        {/* Always show skeleton initially */}
        {(!imageLoaded || !isInView) && !imageError && <ThumbnailSkeleton />}
        
        {/* Progressive loading with blur placeholder if available */}
        {placeholderSrc && !imageLoaded && !imageError && (
          <BlurPlaceholder $src={placeholderSrc} $visible={!imageLoaded} />
        )}
        
        {/* Main image or video */}
        {isInView && cardImage && !imageError ? (
          isVideo(cardImage) ? (
            <VideoThumbnail 
              src={thumbnailSrc}
              autoPlay 
              loop 
              muted 
              playsInline
              preload="metadata" // Only load metadata initially
              onError={(e) => {
                console.error('Video failed to load:', e);
                setImageError(true);
              }}
            />
          ) : (
            <Thumbnail 
              ref={imageRef}
              src={thumbnailSrc} 
              alt={study.title}
              loading="lazy"
              decoding="async" // Decode image asynchronously
              $diagram={isDiagram}
              $loaded={imageLoaded}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                console.error('Image failed to load:', thumbnailSrc);
                setImageError(true);
              }}
            />
          )
        ) : imageError && (
          <ThumbnailFallback>
            {study.title ? study.title.charAt(0).toUpperCase() : '?'}
          </ThumbnailFallback>
        )}
      </ThumbnailWrapper>
      
      <CardContent>
        {study.description && (
          <CardDescription>{study.description}</CardDescription>
        )}
        
        <ViewLink className="view-link">
          View case study
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </ViewLink>
      </CardContent>
    </CardContainer>
  );
};

export default CaseStudyCard;